// src/components/PortalDropdown.jsx
// Shared portal dropdown, extracted from the identical copies that lived in
// TaskAssignment, TestingTask, DevelopmentTask and Deployments.
// Renders `children` in a fixed-positioned card, with a transparent
// full-screen overlay that closes it on an outside click.
//
// Rendering on document.body rather than inside the cell that owns the menu is
// the point of the component. A menu left in the row is a sibling of the cells
// to its right, and those paint over it — which is how the Status menu ended up
// with the Move buttons showing through it on the task grids.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

const MARGIN = 8; // never touch the window edge
const GAP = 6; // breathing room between trigger and menu

/* Pass `anchorRef` — the trigger element — and the menu places ITSELF.
 *
 * The alternative, which every caller here used to do, is to measure the
 * button on click and hand over {top, left}. That has three failure modes,
 * and the Move-to-team menu hit all three at once:
 *
 *   * The numbers are computed ONCE. `position: fixed` does not scroll with
 *     the page, so the moment anything scrolls — the window, or the grid's
 *     own horizontal and vertical scrollers — the menu stays where it was
 *     and the button walks away from it. That is a menu floating in the
 *     middle of the page, attached to nothing.
 *
 *   * The flip-up decision was made against a GUESSED menu height (120px in
 *     three of the callers, 160px in the fourth). The team list is six
 *     entries and taller than either guess, so "there is room below" was
 *     decided on a number that had nothing to do with the menu being placed.
 *
 *   * Nothing clamped the result to the window. A menu that did not fit was
 *     simply cut off by the bottom edge — which is why the Product team was
 *     unreachable from the list.
 *
 * Measuring after render fixes all three: the real height is known, the
 * choice of above-or-below is made on it, the result is clamped into the
 * viewport, and a menu with nowhere to fit scrolls inside itself rather
 * than off the screen. Re-measured on scroll and resize, with capture on so
 * scrolls inside the grid count too.
 *
 * `position` (the old {top, left}) still works for callers that have not
 * moved over, and `style` still overrides everything — a caller that has
 * already worked out its own placement, needs `bottom`, or wants to match
 * the trigger's width, passes that instead. */
const PortalDropdown = ({
  isOpen,
  onClose,
  position,
  style,
  children,
  minWidth = 130,
  anchorRef,
  align = "start", // "start" = left edges line up, "end" = right edges
}) => {
  const menuRef = useRef(null);
  const [placed, setPlaced] = useState(null);

  const place = useCallback(() => {
    const anchor = anchorRef?.current;
    const menu = menuRef.current;
    if (!anchor || !menu) return;

    const a = anchor.getBoundingClientRect();

    /* The trigger is gone — scrolled out of the grid's own scroller, or its
       row re-rendered and this ref now points at a detached node. Either way
       there is nothing left to anchor to, and a menu that keeps placing
       itself against a zero-sized rect lands in the top-left corner and sits
       there looking stuck to the page. Close instead: the thing it belonged
       to is not on screen.

       Checked on the rect rather than isConnected because a node can still be
       in the document while clipped to nothing by an ancestor's overflow. */
    if (a.width === 0 && a.height === 0) {
      onClose?.();
      return;
    }

    const vh = window.innerHeight;
    const vw = window.innerWidth;

    /* offsetHeight, not the rect: the rect is the height AFTER any clamp
       applied on the last pass, so reading it back would ratchet the menu
       smaller on every scroll event until it was a sliver. */
    const h = menu.offsetHeight;
    const w = Math.max(menu.offsetWidth, minWidth);

    const roomBelow = vh - a.bottom - GAP - MARGIN;
    const roomAbove = a.top - GAP - MARGIN;

    // Below by default. Flip up only when it does not fit below AND there is
    // genuinely more room up there — flipping into an even smaller space
    // helps nobody.
    const flipUp = h > roomBelow && roomAbove > roomBelow;

    let top = flipUp ? a.top - GAP - h : a.bottom + GAP;
    top = Math.min(Math.max(MARGIN, top), Math.max(MARGIN, vh - h - MARGIN));

    let left = align === "end" ? a.right - w : a.left;
    left = Math.min(Math.max(MARGIN, left), Math.max(MARGIN, vw - w - MARGIN));

    setPlaced({
      top,
      left,
      // Taller than the window: scroll inside the menu instead of off it.
      maxHeight: Math.max(120, vh - 2 * MARGIN),
    });
  }, [anchorRef, align, minWidth, onClose]);

  /* Layout effect, not effect: this runs before the browser paints, so the
     menu is never seen at the wrong place first. */
  useLayoutEffect(() => {
    if (!isOpen || !anchorRef?.current) return undefined;
    place();
  }, [isOpen, anchorRef, place]);

  useEffect(() => {
    if (!isOpen) return undefined;

    /* Anchored menus re-place; un-anchored ones close.
     *
     * A caller that handed over fixed {top, left} gave a snapshot of where the
     * button was at the moment of the click. Nothing can update that — the
     * component was never told what to follow — so the menu hangs in the page
     * while the row it belongs to scrolls away underneath it. Closing is the
     * only honest option left, and it is what every other menu on the web
     * does in that situation.
     *
     * Capture phase: scroll does not bubble, and the thing that actually moves
     * here is the grid's own scroller, not the window. */
    /* Decided ONCE, and the same function reference is used to add and to
       remove. Re-reading anchorRef in the cleanup could pick the other branch
       if the ref had changed by then, and removeEventListener would silently
       remove nothing — leaving a listener on the window pointing at a menu
       that is gone. */
    const handler = anchorRef?.current ? place : () => onClose?.();

    window.addEventListener("scroll", handler, true);
    window.addEventListener("resize", handler);
    return () => {
      window.removeEventListener("scroll", handler, true);
      window.removeEventListener("resize", handler);
    };
  }, [isOpen, anchorRef, place, onClose]);

  // Reset between openings so a stale position is never shown for a frame.
  useEffect(() => {
    if (!isOpen) setPlaced(null);
  }, [isOpen]);

  if (!isOpen) return null;

  const anchored = Boolean(anchorRef);

  return ReactDOM.createPortal(
    <>
      {/* Transparent overlay to capture outside clicks */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 9999,
          cursor: "default",
        }}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />
      {/* The actual menu */}
      <div
        ref={menuRef}
        className="shadow-lg"
        style={{
          position: "fixed",
          ...(anchored
            ? {
                top: placed?.top ?? 0,
                left: placed?.left ?? 0,
                maxHeight: placed?.maxHeight,
                overflowY: "auto",
                /* Hidden for the one render before it has been measured.
                   It still occupies layout, which is what makes the
                   measurement possible. */
                visibility: placed ? "visible" : "hidden",
              }
            : position
              ? { top: position.top, left: position.left }
              : {}),
          minWidth: `${minWidth}px`,
          backgroundColor: "#fff",
          borderRadius: "8px",
          zIndex: 10000,
          border: "1px solid rgba(0,0,0,0.08)",
          animation: "fadeIn 0.1s ease-out",
          ...style,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </>,
    document.body
  );
};

export default PortalDropdown;
